import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState } from '@/components/EmptyState';
import { Search, Shield, LogIn, LogOut, FilePlus, FileEdit, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import { fmtDateTime } from '@/lib/dates';

const ACTION_META = {
  LOGIN: { icon: LogIn, color: 'text-sky-600 bg-sky-50' },
  LOGOUT: { icon: LogOut, color: 'text-slate-600 bg-slate-100' },
  CREATE: { icon: FilePlus, color: 'text-emerald-600 bg-emerald-50' },
  UPDATE: { icon: FileEdit, color: 'text-amber-600 bg-amber-50' },
  DELETE: { icon: Trash2, color: 'text-red-600 bg-red-50' },
  APPROVE: { icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50' },
  REJECT: { icon: XCircle, color: 'text-red-600 bg-red-50' },
};

export default function AuditTrailPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: 'all', target: 'all', search: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.action !== 'all') params.set('action', filters.action);
      if (filters.target !== 'all') params.set('target_type', filters.target);
      if (filters.search) params.set('search', filters.search);
      const { data } = await api.get(`/audit?${params.toString()}`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.action, filters.target, filters.search]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit Trail Sistem"
        subtitle="Catatan seluruh aktivitas pengguna dalam PROAKTIF — untuk akuntabilitas SPBE."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Audit Trail' }]}
      />

      <Card className="border-slate-200 shadow-premium">
        <CardContent className="p-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="audit-search" placeholder="Cari user, email, deskripsi…" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="pl-9" />
          </div>
          <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
            <SelectTrigger className="w-full md:w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Aksi</SelectItem>
              <SelectItem value="LOGIN">Login</SelectItem>
              <SelectItem value="LOGOUT">Logout</SelectItem>
              <SelectItem value="CREATE">Create</SelectItem>
              <SelectItem value="UPDATE">Update</SelectItem>
              <SelectItem value="DELETE">Delete</SelectItem>
              <SelectItem value="APPROVE">Approve</SelectItem>
              <SelectItem value="REJECT">Reject</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.target} onValueChange={(v) => setFilters({ ...filters, target: v })}>
            <SelectTrigger className="w-full md:w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Objek</SelectItem>
              <SelectItem value="AGENDA">Agenda</SelectItem>
              <SelectItem value="SPPD">SPPD</SelectItem>
              <SelectItem value="USER">User</SelectItem>
              <SelectItem value="OPD">OPD</SelectItem>
              <SelectItem value="SYSTEM">System</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-premium overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>
        ) : items.length === 0 ? (
          <EmptyState title="Tidak ada log" description="Tidak ada aktivitas yang sesuai filter." />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="audit-trail">
              <TableHeader>
                <TableRow className="bg-slate-50/60">
                  <TableHead className="w-44">Waktu</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="w-32">Aksi</TableHead>
                  <TableHead className="w-28 hidden md:table-cell">Objek</TableHead>
                  <TableHead className="hidden lg:table-cell">Deskripsi</TableHead>
                  <TableHead className="hidden xl:table-cell w-32">IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => {
                  const meta = ACTION_META[a.action] || { icon: Shield, color: 'text-slate-600 bg-slate-100' };
                  return (
                    <TableRow key={a.id} className="hover:bg-slate-50">
                      <TableCell className="text-xs font-mono text-slate-600">{fmtDateTime(a.timestamp)}</TableCell>
                      <TableCell>
                        <div className="font-semibold text-slate-800 text-sm">{a.user_name}</div>
                        <div className="text-xs text-slate-500">{a.user_email}</div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${meta.color}`}>
                          <meta.icon className="w-3 h-3" /> {a.action}
                        </span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{a.target_type}</span>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-xs text-slate-600 max-w-md">{a.description}</TableCell>
                      <TableCell className="hidden xl:table-cell text-xs font-mono text-slate-500">{a.ip_address || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
